import { SITE } from "../components/brand";

/**
 * AUTHORS — who wrote a post, and where their identity lives.
 *
 * The canonical record of a person is app/src/content/team.ts in this
 * repo, which renders /about/<slug> and owns the Person JSON-LD. This file is
 * deliberately the thin half of that: a display name and a role, nothing more.
 * Everything else is derived, so there is only one copy of it:
 *
 *   photo    /team/<slug>.webp — the marketing site's own asset. /pub and / are
 *            the SAME ORIGIN (both www.hrmless.dev, different ingress paths), so
 *            a root-relative path resolves without copying any image here.
 *   profile  SITE + /about/<slug> — SITE comes from config/buildEnv.ts, so a dev
 *            build links to dev and a prod build to prod.
 *   entity   SITE + /about/<slug>#person — the @id the marketing site's Person
 *            schema declares. SeoHead references it rather than restating name
 *            and job title, so 227 posts point at ONE entity in the graph
 *            instead of 227 copies that can drift.
 *
 * Adding an author: add a slug here AND a person to team.ts. Both are needed —
 * this file makes the byline render, that one makes the link resolve to a page.
 */

export interface Author {
  readonly slug: string;
  readonly name: string;
  /** Shown under the name in the byline. Keep it in step with team.ts. */
  readonly role: string;
}

/**
 * Keyed by the `author` slug in a post's frontmatter. The slugs match the
 * /about/<slug> URLs exactly — that is what makes the derivation below work.
 */
export const authors: Record<string, Author> = {
  "anthony-tomes": { slug: "anthony-tomes", name: "Anthony Tomes", role: "Co-Founder" },
  "ben-schuster": { slug: "ben-schuster", name: "Ben Schuster", role: "Co-Founder & CTO" },
  "tyler-grant": { slug: "tyler-grant", name: "Tyler Grant", role: "COO" },
  "nicholas-vidal": { slug: "nicholas-vidal", name: "Nicholas Vidal", role: "Principal AI Engineer" },
  "christian-raso": { slug: "christian-raso", name: "Christian Raso", role: "Account Executive" },
};

/**
 * The author a post gets when its frontmatter does not name one.
 *
 * sync_content.py writes this same default into blogPosts.json, so after the
 * next content sync every post carries an explicit author. Resolving it here as
 * well means the byline is correct for the posts already in the registry, which
 * were generated before that field existed — no hand-editing of a generated
 * file, and no build that depends on one having happened.
 */
export const DEFAULT_AUTHOR_SLUG = "anthony-tomes";

/** The author of a post, falling back to the default. Never returns undefined. */
export const authorFor = (slug?: string): Author =>
  (slug && authors[slug]) || authors[DEFAULT_AUTHOR_SLUG];

/** Marketing-site headshot. Root-relative: same origin, no copy of the image. */
export const authorPhoto = (a: Author): string => `/team/${a.slug}.webp`;

/** The person's page — what the byline links to. */
export const authorUrl = (a: Author): string => `${SITE}/about/${a.slug}`;

/** The Person entity's @id, as declared by the marketing site's JSON-LD. */
export const authorEntityId = (a: Author): string => `${SITE}/about/${a.slug}#person`;
